import { useCallback, useState } from "react";
import { describe, type Problem } from "./problem";

/** One mutation at a time from a form or a row: busy flag, the last Problem,
 * a runner that resolves true on success, and one that also hands back the
 * answer and its status for a call whose body matters (a command's result).
 *
 * Every write a person makes from a page goes through this and nothing else
 * (#189): `runWith` is the form for a call whose answer is used. The three
 * writes that do not are not a person's act from a page — the session's
 * sign-in and sign-out (`shell/session.tsx`), the console's session open,
 * which is a connection lifecycle inside an effect with its own phases
 * (`pages/Console.tsx`), and `/dashboard/data`, a read that travels by POST
 * and lives in `useQuery`. */
export function useMutation() {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem>();
  // The Problem rides the answer as well as the lane's state (#639): a
  // sequence of writes reports each step's refusal in its own words, and the
  // lane's `problem` only ever holds the last.
  const runWith = useCallback(async <T,>(call: () => Promise<{ data?: T; error?: unknown; response: Response }>): Promise<{ ok: boolean; status: number; data?: T; problem?: Problem }> => {
    setBusy(true); setProblem(undefined);
    try {
      const { data, error, response } = await call();
      if (response.ok) return { ok: true, status: response.status, data };
      const problem = describe(error, response.status);
      setProblem(problem);
      return { ok: false, status: response.status, problem };
    } catch {
      const problem = describe(undefined);
      setProblem(problem);
      return { ok: false, status: 0, problem };
    } finally { setBusy(false); }
  }, []);
  const run = useCallback(async (call: () => Promise<{ error?: unknown; response: Response }>): Promise<boolean> => (await runWith(call)).ok, [runWith]);
  return { run, runWith, busy, problem, clear: () => setProblem(undefined) };
}
